import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import { sendEmailVerification } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { primaryColor, primaryHoverColor } from '../colors';
import ReCAPTCHA from 'react-google-recaptcha';
import { updateFarmerVerificationStatus } from "../services/farmer/auth";

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [resendHovered, setResendHovered] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [attemptsCount, setAttemptsCount] = useState(0);
  const [maxAttempts, setMaxAttempts] = useState(5);
  const [userEmail, setUserEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [verificationChecked, setVerificationChecked] = useState(false);
  const [userType, setUserType] = useState("farmer"); // Par défaut, on considère l'utilisateur comme un agriculteur

  // Fonction pour gérer la vérification du captcha
  const handleCaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };

  // Récupérer l'adresse IP pour améliorer la sécurité
  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setIpAddress(data.ip);
      } catch (error) {
        console.error("Erreur lors de la récupération de l'adresse IP:", error);
      }
    };
    
    fetchIpAddress();
  }, []);

  // Fonction pour rediriger automatiquement après vérification réussie
  const handleSuccessfulVerification = useCallback(() => {
    setTimeout(() => {
      if (userType === 'buyer') {
        navigate('/sales');
      } else {
        navigate('/farmer-login');
      }
    }, 3000); // Attendre 3 secondes avant la redirection
  }, [userType, navigate]);

  // Vérifier si l'email de l'utilisateur est vérifié et mettre à jour Firestore si nécessaire
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;
    let intervalId;

    const checkEmailVerification = async () => {
      if (!auth.currentUser || verificationChecked) return;

      try {
        setIsLoading(true);
        
        // Forcer le rechargement de l'utilisateur pour obtenir le statut de vérification d'email le plus récent
        await auth.currentUser.reload();
        const user = auth.currentUser;
        
        console.log(`Tentative ${retryCount + 1}: emailVerified = ${user?.emailVerified}`);
        
        if (user && user.emailVerified) {
          // Arrêter les vérifications périodiques
          if (intervalId) {
            clearInterval(intervalId);
          }
          
          // Vérifier si l'utilisateur est un agriculteur ou un acheteur
          let isFarmer = true;
          const farmerDoc = await getDoc(doc(db, "farmers", user.uid));
          
          if (!farmerDoc.exists()) {
            const buyerDoc = await getDoc(doc(db, "buyers", user.uid));
            if (buyerDoc.exists()) {
              isFarmer = false;
              setUserType("buyer");
            } else {
              // L'utilisateur n'est ni un agriculteur ni un acheteur
              setError("Votre compte n'a pas été trouvé dans notre système");
              setIsLoading(false);
              setVerificationChecked(true);
              return;
            }
          }
          
          // Mettre à jour le statut de vérification dans Firestore
          const result = await updateFarmerVerificationStatus(user.uid, isFarmer ? "farmer" : "buyer");
          
          if (result.success) {
            setSuccess(result.message + " Redirection en cours...");
            console.log("Statut de vérification mis à jour avec succès dans Firestore");
            handleSuccessfulVerification(); // Redirection automatique
          } else {
            setError(result.message || "Une erreur est survenue lors de la mise à jour du statut de vérification");
          }
          
          setVerificationChecked(true);
        } else {
          // Si l'email n'est pas encore vérifié, continuer à vérifier périodiquement
          retryCount++;
          if (retryCount >= maxRetries) {
            console.log("Nombre maximum de tentatives atteint");
            setVerificationChecked(true);
            if (intervalId) {
              clearInterval(intervalId);
            }
          }
        }
      } catch (error) {
        console.error("Erreur lors de la vérification de l'email:", error);
        setError("Une erreur est survenue lors de la vérification de votre email");
        retryCount++;
        if (retryCount >= maxRetries) {
          setVerificationChecked(true);
          if (intervalId) {
            clearInterval(intervalId);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    // Vérification initiale
    checkEmailVerification();
    
    // Vérifications périodiques toutes les 3 secondes
    intervalId = setInterval(() => {
      if (!verificationChecked && retryCount < maxRetries) {
        checkEmailVerification();
      } else {
        clearInterval(intervalId);
      }
    }, 3000);
    
    // Nettoyage
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [verificationChecked, handleSuccessfulVerification]);

  // Récupérer l'email depuis l'état de navigation ou utiliser l'utilisateur actuel
  useEffect(() => {
    // Tenter de récupérer l'email depuis l'état de navigation (si disponible)
    const email = location.state?.email || "";
    
    if (email) {
      setUserEmail(email);
    } else if (auth.currentUser) {
      // Sinon, utiliser l'email de l'utilisateur actuellement connecté
      setUserEmail(auth.currentUser.email || "");
    }
    
    // Récupérer les données de tentatives depuis localStorage
    const emailVerifData = JSON.parse(localStorage.getItem('emailVerificationData') || '{}');
    if (emailVerifData.cooldownUntil && emailVerifData.cooldownUntil > Date.now()) {
      // Calculer le temps restant en secondes
      const timeLeft = Math.ceil((emailVerifData.cooldownUntil - Date.now()) / 1000);
      setResendCooldown(timeLeft);
    }
    
    if (emailVerifData.attempts) {
      setAttemptsCount(emailVerifData.attempts);
    }
  }, [location.state]);

  // Gérer le cooldown pour le renvoi d'email
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            // Mettre à jour localStorage quand le cooldown est terminé
            const emailVerifData = JSON.parse(localStorage.getItem('emailVerificationData') || '{}');
            delete emailVerifData.cooldownUntil;
            localStorage.setItem('emailVerificationData', JSON.stringify(emailVerifData));
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendCooldown]);
  
  // Calculer le délai de cooldown en fonction du nombre de tentatives
  const calculateCooldown = (attempts) => {
    // Délai exponentiel: 1min, 5min, 15min, 30min, 60min
    const delays = [60, 300, 900, 1800, 3600];
    const index = Math.min(attempts - 1, delays.length - 1);
    return delays[index];
  };

  // Fonction pour renvoyer l'email de vérification
  const handleResendEmail = async () => {
    if (resendCooldown > 0 || !userEmail) return;
    
    // Vérifier si le captcha a été validé
    if (!captchaVerified) {
      setError("Veuillez valider le captcha pour prouver que vous n'êtes pas un robot");
      return;
    }
    
    // Vérifier si le nombre maximum de tentatives a été atteint
    if (attemptsCount >= maxAttempts) {
      setError(`Nombre maximum de tentatives atteint. Veuillez réessayer plus tard ou contacter le support.`);
      return;
    }
    
    try {
      setIsLoading(true);
      setError("");
      setSuccess("");
      
      const user = auth.currentUser;
      if (user) {
        await sendEmailVerification(user, {
          url: `${window.location.origin}/email-verification`,
        });
        
        // Incrémenter le compteur de tentatives
        const newAttemptCount = attemptsCount + 1;
        setAttemptsCount(newAttemptCount);
        
        // Calculer le délai de cooldown en fonction du nombre de tentatives
        const cooldownSeconds = calculateCooldown(newAttemptCount);
        const cooldownUntil = Date.now() + (cooldownSeconds * 1000);
        
        // Stocker les données dans localStorage avec l'adresse IP pour une meilleure sécurité
        localStorage.setItem('emailVerificationData', JSON.stringify({
          attempts: newAttemptCount,
          cooldownUntil: cooldownUntil,
          email: userEmail,
          ip: ipAddress,
          lastAttempt: Date.now()
        }));
        
        setSuccess(`Un nouvel email de vérification a été envoyé. Tentative ${newAttemptCount}/${maxAttempts}.`);
        setResendCooldown(cooldownSeconds);
      } else {
        setError("Vous devez être connecté pour renvoyer l'email de vérification");
      }
    } catch (error) {
      setError("Erreur lors de l'envoi de l'email. Veuillez réessayer plus tard.");
      console.error("Erreur d'envoi d'email:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour rediriger vers la page appropriée selon le type d'utilisateur
  const handleRedirectToLogin = () => {
    if (userType === 'buyer') {
      navigate('/sales'); // Rediriger les acheteurs vers la page des ventes
    } else {
      navigate('/farmer-login'); // Rediriger les agriculteurs vers la page de connexion
    }
  };



  // Fonction pour vérifier manuellement le statut de vérification
  const handleManualCheck = async () => {
    if (!auth.currentUser) {
      setError("Vous devez être connecté pour vérifier votre statut");
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      setSuccess("");
      
      // Forcer le rechargement de l'utilisateur
      await auth.currentUser.reload();
      const user = auth.currentUser;
      
      console.log(`Vérification manuelle: emailVerified = ${user?.emailVerified}`);
      
      if (user && user.emailVerified) {
        // Vérifier si l'utilisateur est un agriculteur ou un acheteur
        let isFarmer = true;
        const farmerDoc = await getDoc(doc(db, "farmers", user.uid));
        
        if (!farmerDoc.exists()) {
          const buyerDoc = await getDoc(doc(db, "buyers", user.uid));
          if (buyerDoc.exists()) {
            isFarmer = false;
            setUserType("buyer");
          } else {
            setError("Votre compte n'a pas été trouvé dans notre système");
            return;
          }
        }
        
        // Mettre à jour le statut de vérification dans Firestore
        const result = await updateFarmerVerificationStatus(user.uid, isFarmer ? "farmer" : "buyer");
        
        if (result.success) {
          setSuccess(result.message + " Redirection en cours...");
          setVerificationChecked(true);
          console.log("Vérification manuelle réussie - Statut mis à jour dans Firestore");
          handleSuccessfulVerification(); // Redirection automatique
        } else {
          setError(result.message || "Une erreur est survenue lors de la mise à jour du statut de vérification");
        }
      } else {
        setError("Votre email n'est pas encore vérifié. Veuillez cliquer sur le lien dans l'email de vérification que vous avez reçu.");
      }
    } catch (error) {
      console.error("Erreur lors de la vérification manuelle:", error);
      setError("Une erreur est survenue lors de la vérification de votre statut");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="text-center p-6 max-w-md mx-auto bg-white shadow-lg rounded-lg my-8">
      <div className="mt-2">
        <h3 className="text-gray-800 text-xl font-semibold sm:text-2xl mt-4">Vérification de votre email</h3>
      </div>
      
      <div className="text-2xl font-bold pt-8 text-green-700">
        Veuillez vérifier votre email pour confirmer votre inscription
      </div>
      
      {error && (
        <div className="mt-4 p-3 bg-red-100 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mt-4 p-3 bg-green-100 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}
      
      {attemptsCount > 0 && attemptsCount < maxAttempts && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-lg">
          <p>Tentatives d'envoi: <span className="font-semibold">{attemptsCount}/{maxAttempts}</span></p>
        </div>
      )}
      
      {attemptsCount >= maxAttempts && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <p>Nombre maximum de tentatives atteint. Veuillez contacter le support si vous n'avez pas reçu l'email.</p>
        </div>
      )}
      
      <div className="mt-6 text-gray-700 bg-gray-50 p-4 rounded-lg">
        {userEmail ? (
          <p>Un email de vérification a été envoyé à <span className="font-semibold text-green-700">{userEmail}</span></p>
        ) : (
          <p>Un email de vérification a été envoyé à l'adresse que vous avez fournie lors de l'inscription</p>
        )}
        <p className="mt-3">Cliquez sur le lien contenu dans cet email pour valider votre compte.</p>
        <p className="mt-3"><strong>Important :</strong> Après la vérification de votre email, votre compte devra être validé par un administrateur avant que vous puissiez vous connecter.</p>
        <p className="mt-3 text-sm text-gray-600">
          N'oubliez pas de vérifier vos dossiers de spam ou courriers indésirables si vous ne trouvez pas l'email dans votre boîte de réception.
        </p>
      </div>
      
      {/* Information sur le processus de vérification */}
      <div className="mt-4 text-gray-700 bg-blue-50 border border-blue-200 p-4 rounded-lg">
        <h4 className="font-semibold text-blue-800 mb-2">📧 Processus de vérification</h4>
        <ol className="text-sm space-y-1 list-decimal list-inside">
          <li>Cliquez sur le lien de vérification dans votre email</li>
          <li>Revenez sur cette page après avoir cliqué sur le lien</li>
          <li>Utilisez le bouton "Vérifier mon statut" pour confirmer la vérification</li>
          <li>Une fois vérifié, vous pourrez vous connecter</li>
        </ol>
        <p className="mt-3 text-xs text-blue-600">
          <strong>Note :</strong> La vérification peut prendre quelques secondes supplémentaires. 
          Le système vérifie automatiquement votre statut toutes les 3 secondes, mais vous pouvez aussi utiliser le bouton de vérification manuelle.
        </p>
      </div>
      
      {/* Ajout du captcha pour la vérification */}
      <div className="mt-6 flex justify-center">
        <ReCAPTCHA
          sitekey={process.env.NODE_ENV === 'development' 
            ? process.env.REACT_APP_RECAPTCHA_TEST_KEY 
            : process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          onChange={handleCaptchaChange}
          className="mx-auto"
        />
      </div>

      <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8 mb-4">
        {/* Bouton renvoyer l'email désactivé si pas d'utilisateur connecté */}
        {auth.currentUser && (
          <button
            type="button"
            onClick={handleResendEmail}
            disabled={isLoading || resendCooldown > 0}
            className="px-6 py-3 font-medium rounded-lg transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: resendHovered ? primaryHoverColor : primaryColor,
              color: '#fff',
              opacity: (isLoading || resendCooldown > 0) ? 0.7 : 1,
              focusRingColor: primaryColor
            }}
            onMouseEnter={() => setResendHovered(true)}
            onMouseLeave={() => setResendHovered(false)}
          >
            {resendCooldown > 0
              ? `Renvoyer l'email (${resendCooldown}s)`
              : attemptsCount >= maxAttempts
                ? `Limite de tentatives atteinte`
                : `Renvoyer l'email de vérification (${attemptsCount}/${maxAttempts})`}
          </button>
        )}
        
        {/* Bouton de vérification manuelle */}
        {auth.currentUser && (
          <button
            type="button"
            onClick={handleManualCheck}
            disabled={isLoading || verificationChecked}
            className="px-6 py-3 font-medium rounded-lg transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isLoading ? "Vérification..." : verificationChecked ? "Vérifié" : "Vérifier mon statut"}
          </button>
        )}
        
        <button
          type="button"
          onClick={handleRedirectToLogin}
          className="px-6 py-3 font-medium rounded-lg transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:shadow-md"
          style={{
            backgroundColor: isHovered ? primaryHoverColor : primaryColor,
            color: '#fff',
            focusRingColor: primaryColor
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {userType === 'buyer' ? 'Retour aux ventes' : 'Se connecter'}
        </button>
      </div>
    </div>
  );
};

export default EmailVerification;